import { Customer } from '../types';
import fulfill from '../../fulfill';
import { SideEffectFunction } from '../../sideEffects';
import { CORE_API_HOSTNAME } from '../../env';

import {
    CustomerNotFoundResponse,
    CustomerResponse,
    CustomerTokensResponse,
    EditCustomerField,
    FetchEditCustomerFieldResponse,
    GetCustomer,
    GetCustomerTokens,
    FetchDeleteCustomer,
    FetchDeleteCustomerResponse,
    ActionTypes,
    ChangeCustomerId,
    ChangeCustomerIdError,
    ToggleChangeCustomerIdModal
} from './actions';
import { CustomerToken } from '../../types/customers';

type FetchCustomerResponse = CustomerResponse | CustomerNotFoundResponse;

const fetchCustomer: SideEffectFunction<GetCustomer, FetchCustomerResponse> = async (action) => {
    const { accountId, id } = action.payload;
    const result: FetchCustomerResponse = await fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/customers/users/${id}`,
        handlers: {
            200: (customer: Customer) => ({
                type: ActionTypes.CustomerResponse,
                payload: {
                    customer,
                },
                meta: {
                    cause: action,
                },
            }),
            404: () => ({
                type: ActionTypes.CustomerNotFoundResponse,
                meta: {
                    cause: action,
                },
            }),
        },
    });

    return result;
};

const fetchCustomerTokens: SideEffectFunction<GetCustomerTokens, CustomerTokensResponse> = async (action) => {
    const { accountId, id } = action.payload;
    const result: CustomerTokensResponse = await fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/customers/users/${encodeURIComponent(id)}/tokens?limit=100&include_deleted=false`,
        handlers: {
            200: (tokens: CustomerToken[]) => {
                return {
                    type: ActionTypes.CustomerTokensResponse,
                    payload: {
                        tokens: tokens,
                    },
                    meta: {
                        cause: action,
                    },
                };
            },
            404: () => ({
                type: ActionTypes.CustomerTokensResponse,
                payload: {
                    tokens: [],
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });

    return result;
};

const fetchEditCustomerField: SideEffectFunction<EditCustomerField, FetchEditCustomerFieldResponse> = async (
    action
) => {
    const { accountId, id, value } = action.payload;
    const result: FetchEditCustomerFieldResponse = await fulfill.put({
        accountId: accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/customers/users/${id}`,
        json: value,
        handlers: {
            200: (customer: Customer) => ({
                type: ActionTypes.FetchEditCustomerFieldResponse,
                payload: {
                    customer,
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

const fetchDeleteCustomer: SideEffectFunction<FetchDeleteCustomer, FetchDeleteCustomerResponse> = async (action) => {
    const { accountId, customerId } = action.payload;
    return fulfill.delete({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/customers/users/${encodeURIComponent(customerId)}?delete_tokens=true`,
        handlers: {
            200: (customer: Customer) => ({
                type: ActionTypes.FetchDeleteCustomerResponse,
                payload: {},
                meta: {
                    cause: action,
                },
            }),
        },
    });
};

const fetchChangeCustomerId: SideEffectFunction<ChangeCustomerId, ToggleChangeCustomerIdModal | ChangeCustomerIdError> = async (action) => {
    const { accountId, oldCustomerId, newCustomerId } = action.payload;
    return fulfill.post({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/customers/users/${encodeURIComponent(oldCustomerId)}/change_customer_id`,
        json: {
            customer_id: newCustomerId,
        },
        handlers: {
            200: () => {
                window.location.pathname = `${accountId}/customers/${newCustomerId}`;
                return {
                    type: ActionTypes.ToggleChangeCustomerIdModal,
                    payload: {
                        open: false,
                    },
                };
            },
            400: () => {
                return {
                    type: ActionTypes.ChangeCustomerIdError,
                    payload: {
                        newCustomerId: newCustomerId,
                    },
                };
            },
        },
    });
};


const effects = {
    [ActionTypes.GetCustomer]: [fetchCustomer],
    [ActionTypes.GetCustomerTokens]: [fetchCustomerTokens],
    [ActionTypes.EditCustomerField]: [fetchEditCustomerField],
    [ActionTypes.FetchDeleteCustomer]: [fetchDeleteCustomer],
    [ActionTypes.ChangeCustomerId]: [fetchChangeCustomerId],
};

export default effects;
